import { Store } from '@ngrx/store';
import * as angular from 'angular';
import { IController } from 'angular';
import * as _ from 'lodash';
import { PlannedDiplomaCredentials } from '../../../../ng2/shared/constants/planned-diploma-credentials.constant';
import { TValidDiplomaCredentials } from '../../../../ng2/shared/constants/planned-diploma-credentials.constant';
import { PlannedDiplomaEndorsements } from '../../../../ng2/shared/constants/planned-diploma-endorsements.constant';
import { TValidDiplomaEndorsements } from '../../../../ng2/shared/constants/planned-diploma-endorsements.constant';
import { IStudent } from '../../../../ng2/shared/typings/interfaces/student.interface';
import { UpdateSingleStudentThroughSDCStoreSuccess } from '../../../../ng2/store/actions/single-student-actions';

interface IType {
  readonly endorsement: 'endorsement';
  readonly credential: 'credential';
}

interface IBulkPatch {
  _ids: String[];
  path: String;
  newValue: String[];
}

export class PlannedDiplomaOptionsModalCtrl implements IController {
  static $inject = [
    '$mdDialog',
    'sdcActions',
    'BackgroundJobSpinner',
    'ErrorModal',
    'selectedStudentIds',
    'selectedStudents',
    'Store',
  ];

  // class properties -- public, private, protected
  public exists;
  public isIndeterminate;
  public endorsements: any;
  public credentials: any;

  private type: IType;
  private pullOut;
  private pushIn;
  private updateInDeterminate;
  private combinedEndorsements: any[];
  private combinedCredentials: any[];
  private patchEndorsements: any[];
  private patchCredentials: any[];
  private helpers;

  constructor(
    private $mdDialog: any,
    private sdcActions: any,
    private BackgroundJobSpinner: any,
    private ErrorModal: any,
    private selectedStudentIds: any,
    private selectedStudents: any,
    private store: Store<any>,
  ) {
    this.endorsements = angular.copy(PlannedDiplomaEndorsements);
    this.credentials = angular.copy(PlannedDiplomaCredentials);

    this.type = {
      endorsement: 'endorsement',
      credential: 'credential',
    } as IType;

    this.patchEndorsements = [];
    this.patchCredentials = [];

    // grad planning details
    this.combinedEndorsements = _.map(this.selectedStudents, (student: IStudent) => {
      return student.gradPlanningDetails.plannedEndorsements;
    });
    this.combinedCredentials = _.map(this.selectedStudents, (student: IStudent) => {
      return student.gradPlanningDetails.plannedCreds;
    });
    this.combinedEndorsements = _.sortBy(_.uniq(_.flatten(this.combinedEndorsements)));
    this.combinedCredentials = _.sortBy(_.uniq(_.flatten(this.combinedCredentials)));

    // return true if the value exists in the initial endorsements or credentials array or in the related patch
    this.exists = (value: string, type: string) => {
      if (type === this.type.endorsement) {
        return _.includes(this.combinedEndorsements, value) || _.includes(this.patchEndorsements, value);
      } else if (type === this.type.credential) {
        return _.includes(this.combinedCredentials, value) || _.includes(this.patchCredentials, value);
      }
    };

    // sets indeterminate state on load
    this.isIndeterminate = (value: string, type: string) => {
      return _.some(this.selectedStudents, (student: IStudent) => {
        if (type === this.type.endorsement) {
          if (this.exists(value, this.type.endorsement) === true) {
            return !_.includes(student.gradPlanningDetails.plannedEndorsements, value);
          }
        } else {
          if (this.exists(value, this.type.credential) === true) {
            return !_.includes(student.gradPlanningDetails.plannedCreds, value);
          }
        }
      });
    };

    this.updateInDeterminate = (patch, constant, type: string) => {
      let obj;
      _.each(patch, item => {
        obj = _.filter(constant, (v: any) => {
          return v.human === item;
        });
        if (this.isIndeterminate(obj[0].human, type)) {
          obj[0].checkbox.isIndeterminate = false;
        }
      });
    };

    this.pullOut = (value: string, type: string) => {
      if (type === this.type.endorsement) {
        _.pull(this.combinedEndorsements, value);
        _.pull(this.patchEndorsements, value);
      } else if (type === this.type.credential) {
        _.pull(this.combinedCredentials, value);
        _.pull(this.patchCredentials, value);
      }
    };

    this.pushIn = (value: string, type: string) => {
      if (type === this.type.endorsement) {
        this.combinedEndorsements.unshift(value);
        this.patchEndorsements.unshift(value);
        this.updateInDeterminate(this.patchEndorsements, this.endorsements, this.type.endorsement);
      } else if (type === this.type.credential) {
        this.combinedCredentials.unshift(value);
        this.patchCredentials.unshift(value);
        this.updateInDeterminate(this.patchCredentials, this.credentials, this.type.credential);
      }
    };

    this.helpers = {
      saving: {
        createPatchString(arr): string {
          return JSON.stringify(_.sortBy(_.uniq(arr)));
        },

        convertValuesToKeys(obj, _id: string, string: string): object {
          if (obj[string]) {
            obj[string].push(_id);
          } else {
            obj[string] = [_id];
          }
          return obj;
        },
      },
    };
  }

  $onInit() {
    //
  }

  toggle(value: string, type: string): any {
    if (this.exists(value, type)) {
      this.pullOut(value, type);
    } else {
      this.pushIn(value, type);
    }
  }

  cancel() {
    this.$mdDialog.hide();
  }

  runBackgroundJob({ patches, newEndorsements, newCredentials }) {
    return this.sdcActions
      .multiUpdateStudentAsync(patches) // pass options in here to specify not to update mongo through student model
      .then(
        this.$mdDialog.hide({
          action: 'BULK_EDIT_EDORS_CREDS',
          newEndorsements,
          newCredentials,
        }),
      )
      .catch(errorResponse => {
        this.ErrorModal.show('Unable to save your changes', errorResponse);
      });
  }

  dispatchActionToUpdateStore(patches) {
    const [student] = this.selectedStudents;
    const studentWithAppliedPatch = this.applyPatchToStudent(student, patches);
    const newObj = Object.assign({}, studentWithAppliedPatch);
    this.store.dispatch(new UpdateSingleStudentThroughSDCStoreSuccess(newObj));
  }

  applyPatchToStudent(student, patch: IBulkPatch[]) {
    const { _id } = student;

    const newEndorsementValuesPatch = _.find(patch, ({ _ids, path }) => {
      return _.includes(_ids, _id) && path === 'gradPlanningDetails.plannedEndorsements';
    });
    const newCredentialValuesPatch = _.find(patch, ({ _ids, path }) => {
      return _.includes(_ids, _id) && path === 'gradPlanningDetails.plannedCreds';
    });

    student.gradPlanningDetails.plannedEndorsements = newEndorsementValuesPatch.newValue;
    student.gradPlanningDetails.plannedCreds = newCredentialValuesPatch.newValue;

    return student;
  }

  save() {
    let uniqueEndorsements = {};
    let uniqueCredentials = {};
    let newEndorsements: TValidDiplomaEndorsements[];
    let newCredentials: TValidDiplomaCredentials[];

    _.each(this.selectedStudents, (student: IStudent) => {
      const existingEndorsements = student.gradPlanningDetails.plannedEndorsements;
      const existingCredentials = student.gradPlanningDetails.plannedCreds;

      // set updated endorsements / cred arrays for each student
      if (existingEndorsements.length > this.patchEndorsements.length) {
        newEndorsements = _.intersection(
          existingEndorsements.concat(this.patchEndorsements),
          this.combinedEndorsements,
        );
      } else {
        newEndorsements = existingEndorsements.concat(this.patchEndorsements);
      }

      if (existingCredentials.length > this.patchCredentials.length) {
        newCredentials = _.intersection(existingCredentials.concat(this.patchCredentials), this.combinedCredentials);
      } else {
        newCredentials = existingCredentials.concat(this.patchCredentials);
      }

      // stringify arrays
      const endorsementsAsKey = this.helpers.saving.createPatchString(newEndorsements);
      const credentialsAsKey = this.helpers.saving.createPatchString(newCredentials);

      // convert stringified arrays to new objects for use in patch construction
      uniqueEndorsements = this.helpers.saving.convertValuesToKeys(uniqueEndorsements, student._id, endorsementsAsKey);
      uniqueCredentials = this.helpers.saving.convertValuesToKeys(uniqueCredentials, student._id, credentialsAsKey);
    });

    // create patch objects
    const patches = [];
    const _ids = '_ids';
    const path = 'path';
    const newValue = 'newValue';

    _.each(uniqueEndorsements, (v, k) => {
      let patchEndorsementsObj;
      patchEndorsementsObj = {};
      patchEndorsementsObj[_ids] = v;
      patchEndorsementsObj[path] = 'gradPlanningDetails.plannedEndorsements';
      patchEndorsementsObj[newValue] = JSON.parse(`${k}`);
      patches.push(patchEndorsementsObj);
    });

    _.each(uniqueCredentials, (v, k) => {
      let patchCredentialsObj;
      patchCredentialsObj = {};
      patchCredentialsObj[_ids] = v;
      patchCredentialsObj[path] = 'gradPlanningDetails.plannedCreds';
      patchCredentialsObj[newValue] = JSON.parse(`${k}`);
      patches.push(patchCredentialsObj);
    });

    const backgroundJob = this.runBackgroundJob({ patches, newEndorsements, newCredentials }).then(() =>
      this.dispatchActionToUpdateStore(patches),
    );

    this.BackgroundJobSpinner.activateSpinner('Updating diploma options...', backgroundJob);
  }
}
